import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import practitionerService from '../services/PractitionerService';
import { Card, Button, Input, SearchInput, Table, Modal, Select, Alert } from '../components/ui';

/**
 * PractitionersPage component for managing practitioners
 * @returns {React.ReactElement} PractitionersPage component
 */
const PractitionersPage = () => {
  // State for practitioners data
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // State for creating/editing practitioners
  const [showPractitionerModal, setShowPractitionerModal] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    gender: '',
    age: '',
    title: '',
    education: '',
    certifications: '',
    experienceYears: '',
    workHistory: '',
    honors: '',
    avatarUrl: '',
    phoneNumber: '',
    email: '',
    specialties: '',
    bio: '',
    languages: '',
    consultationFee: '',
    tags: '',
    wechat: '',
    address: '',
    scheduleNote: '',
    isExpert: false,
    sortOrder: 0,
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  const { hasPermission } = useAuth();
  const canManagePractitioners = hasPermission('practitioners', 'write');

  // Fetch practitioners on component mount
  useEffect(() => {
    fetchPractitioners();
  }, []);

  // Function to fetch practitioners
  const fetchPractitioners = async () => {
    setLoading(true);
    try {
      const data = practitionerService.getAllPractitioners();

      // 检查第一个调理师的详细信息
      if (data.length > 0) {
        const details = practitionerService.getPractitionerDetails(data[0].id);

      }

      setPractitioners(data);
      setError(null);
    } catch (err) {

      setError('获取调理师数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchTerm('');
  };

  // Filter and search practitioners
  const filteredPractitioners = useMemo(() => {

    const filtered = practitioners.filter(practitioner => {
      // Get detailed information for search
      const details = practitionerService.getPractitionerDetails(practitioner.id) || {};
      const specialtiesText = Array.isArray(details.specialties)
        ? details.specialties.join(' ')
        : details.specialties || '';

      const matchesSearch =
        practitioner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        details.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        specialtiesText.toLowerCase().includes(searchTerm.toLowerCase()) ||
        details.bio?.toLowerCase().includes(searchTerm.toLowerCase());

      if (filter === 'all') return matchesSearch;
      if (filter === 'active') return matchesSearch && practitioner.isActive;
      if (filter === 'inactive') return matchesSearch && !practitioner.isActive;
      return matchesSearch;
    });

    return filtered;
  }, [practitioners, searchTerm, filter]);

  // Handle modal open for new practitioner
  const handleAddNewPractitioner = () => {
    setSelectedPractitioner(null);
    setFormData({
      username: '',
      password: '',
      name: '',
      gender: '',
      age: '',
      title: '',
      education: '',
      certifications: '',
      experienceYears: '',
      workHistory: '',
      honors: '',
      avatarUrl: '',
      phoneNumber: '',
      email: '',
      specialties: '',
      bio: '',
      languages: '',
      consultationFee: '',
      tags: '',
      wechat: '',
      address: '',
      scheduleNote: '',
      isExpert: false,
      sortOrder: 0,
      isActive: true
    });
    setFormErrors({});
    setShowPractitionerModal(true);
  };

  // Handle modal open for edit practitioner
  const handleEditPractitioner = async (practitioner) => {
    setSelectedPractitioner(practitioner);

    // Get detailed practitioner info including professional details
    const practitionerDetails = practitionerService.getPractitionerDetails(practitioner.id);

    setFormData({
      username: practitioner.username || '',
      password: '',
      name: practitioner.name || '',
      gender: practitioner.gender || '',
      age: practitioner.age || '',
      title: practitionerDetails?.title || '',
      education: practitionerDetails?.education || '',
      certifications: Array.isArray(practitionerDetails?.certifications) ? practitionerDetails.certifications.join(', ') : practitionerDetails?.certifications || '',
      experienceYears: practitionerDetails?.experienceYears || '',
      workHistory: Array.isArray(practitionerDetails?.workHistory) ? practitionerDetails.workHistory.map(w => `${w.organization} (${w.title}, ${w.from}-${w.to})`).join('; ') : '',
      honors: Array.isArray(practitionerDetails?.honors) ? practitionerDetails.honors.join(', ') : practitionerDetails?.honors || '',
      avatarUrl: practitionerDetails?.avatarUrl || '',
      phoneNumber: practitioner.phoneNumber || practitioner.phone || '',
      email: practitioner.email || '',
      specialties: Array.isArray(practitionerDetails?.specialties) ? practitionerDetails.specialties.join(', ') : practitionerDetails?.specialties || '',
      bio: practitionerDetails?.bio || practitionerDetails?.introduction || '',
      languages: Array.isArray(practitionerDetails?.languages) ? practitionerDetails.languages.join(', ') : practitionerDetails?.languages || '',
      consultationFee: practitionerDetails?.consultationFee || '',
      tags: Array.isArray(practitionerDetails?.tags) ? practitionerDetails.tags.join(', ') : practitionerDetails?.tags || '',
      wechat: practitionerDetails?.wechat || '',
      address: practitionerDetails?.address || '',
      scheduleNote: practitionerDetails?.scheduleNote || '',
      isExpert: practitionerDetails?.isExpert || false,
      sortOrder: practitionerDetails?.sortOrder || 0,
      isActive: practitioner.isActive !== undefined ? practitioner.isActive : true
    });
    setFormErrors({});
    setShowPractitionerModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入调理师姓名';
    }

    if (!selectedPractitioner) {
      // For new practitioners, username and password are required
      if (!formData.username.trim()) {
        errors.username = '请输入用户名';
      }
      if (!formData.password.trim()) {
        errors.password = '请输入密码';
      } else if (formData.password.length < 6) {
        errors.password = '密码至少需要6个字符';
      }
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '请输入有效的邮箱地址';
    }

    if (formData.phoneNumber && !/^1[3-9]\d{9}$/.test(formData.phoneNumber.replace(/\s|-/g, ''))) {
      errors.phoneNumber = '请输入有效的手机号码';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (selectedPractitioner) {
        // Update existing practitioner
        // Separate user data and professional details
        const userData = {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          phone: formData.phoneNumber, // Also set phone for compatibility
          isActive: formData.isActive
        };

        const professionalDetails = {
          title: formData.title,
          gender: formData.gender,
          age: formData.age,
          education: formData.education,
          certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()).filter(s => s) : [],
          experienceYears: formData.experienceYears,
          workHistory: formData.workHistory ? formData.workHistory.split(';').map(s => {
            const match = s.match(/(.+?) \((.+?), (.+?)-(.*?)\)/);
            return match ? { organization: match[1].trim(), title: match[2].trim(), from: match[3].trim(), to: match[4].trim() } : null;
          }).filter(Boolean) : [],
          honors: formData.honors ? formData.honors.split(',').map(s => s.trim()).filter(s => s) : [],
          avatarUrl: formData.avatarUrl,
          specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(s => s) : [],
          bio: formData.bio,
          languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(s => s) : [],
          consultationFee: formData.consultationFee,
          tags: formData.tags ? formData.tags.split(',').map(s => s.trim()).filter(s => s) : [],
          wechat: formData.wechat,
          address: formData.address,
          scheduleNote: formData.scheduleNote,
          isExpert: formData.isExpert,
          sortOrder: formData.sortOrder
        };

        // Update user information
        const userResult = practitionerService.updatePractitioner(selectedPractitioner.id, userData);
        if (!userResult.success) {
          throw new Error(userResult.message || '更新用户信息失败');
        }

        // Update professional details
        practitionerService.setPractitionerDetails(selectedPractitioner.id, professionalDetails);
      } else {
        // Create new practitioner
        const userData = {
          username: formData.username,
          password: formData.password,
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          phone: formData.phoneNumber, // Also set phone for compatibility
          isActive: formData.isActive
        };

        // Set professional details for the new practitioner
        const professionalDetails = {
          title: formData.title,
          gender: formData.gender,
          age: formData.age,
          education: formData.education,
          certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()).filter(s => s) : [],
          experienceYears: formData.experienceYears,
          workHistory: formData.workHistory ? formData.workHistory.split(';').map(s => {
            const match = s.match(/(.+?) \((.+?), (.+?)-(.*?)\)/);
            return match ? { organization: match[1].trim(), title: match[2].trim(), from: match[3].trim(), to: match[4].trim() } : null;
          }).filter(Boolean) : [],
          honors: formData.honors ? formData.honors.split(',').map(s => s.trim()).filter(s => s) : [],
          avatarUrl: formData.avatarUrl,
          specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(s => s) : [],
          bio: formData.bio,
          languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(s => s) : [],
          consultationFee: formData.consultationFee,
          tags: formData.tags ? formData.tags.split(',').map(s => s.trim()).filter(s => s) : [],
          wechat: formData.wechat,
          address: formData.address,
          scheduleNote: formData.scheduleNote,
          isExpert: formData.isExpert,
          sortOrder: formData.sortOrder
        };

        const result = practitionerService.createPractitioner(userData);
        if (!result.success) {
          throw new Error(result.message || '创建调理师失败');
        }

        // Set professional details for the new practitioner
        practitionerService.setPractitionerDetails(result.user.id, professionalDetails);
      }

      // Refresh practitioner list
      fetchPractitioners();
      setShowPractitionerModal(false);
    } catch (err) {
      setFormErrors({ submit: err.message || '保存调理师信息失败，请重试' });
    }
  };

  // Handle practitioner delete
  const handleDeletePractitioner = (practitionerId) => {
    if (window.confirm('确定要删除此调理师吗？此操作无法撤销。')) {
      try {
        practitionerService.deletePractitioner(practitionerId);
        fetchPractitioners();
      } catch (err) {
        alert('删除调理师失败');
      }
    }
  };

  // Handle toggle practitioner status
  const handleToggleStatus = (practitioner) => {
    try {
      const updatedPractitioner = {
        ...practitioner,
        isActive: !practitioner.isActive
      };
      practitionerService.updatePractitioner(practitioner.id, updatedPractitioner);
      fetchPractitioners();
    } catch (err) {
      alert('更新调理师状态失败');
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: 'name',
      title: '姓名',
      render: (cellValue, practitioner, rowIndex) => {
        if (!practitioner) return null;
        const details = practitionerService.getPractitionerDetails(practitioner.id) || {};
        const avatarUrl = details.avatarUrl;
        // 检查图片是否可用，否则用首字母
        const [imgError, setImgError] = React.useState(false);
        const showImage = avatarUrl && !imgError;
        return (
          <div className="flex items-center space-x-4 min-w-[180px]">
            <div className="flex-shrink-0">
              {showImage ? (
                <img
                  src={avatarUrl}
                  alt={practitioner.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-emerald-200 shadow"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white text-lg font-bold shadow">
                  {practitioner.name ? practitioner.name.charAt(0) : '?'}
                </div>
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-base">{practitioner.name || '未知'}</div>
              {details.title && (
                <div className="text-xs text-emerald-600 mt-0.5">{details.title}</div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'specialties',
      title: '专长',
      render: (cellValue, practitioner) => {
        if (!practitioner) return null;
        const details = practitionerService.getPractitionerDetails(practitioner.id) || {};
        const specialties = details.specialties;
        const displayText = Array.isArray(specialties) ? specialties.join(', ') : specialties;
        return displayText ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            {displayText}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">未设置</span>
        );
      }
    },
    {
      key: 'tags',
      title: '标签',
      render: (cellValue, practitioner) => {
        if (!practitioner) return null;
        const details = practitionerService.getPractitionerDetails(practitioner.id) || {};
        const tags = details.tags;
        return tags && tags.length ? (
          <div className="flex flex-wrap gap-1">{tags.map(tag => <span key={tag} className="inline-block bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs">{tag}</span>)}</div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      }
    },
    {
      key: 'status',
      title: '状态',
      render: (cellValue, practitioner) => (
        practitioner ? (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${practitioner.isActive
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-gray-50 text-gray-700 border border-gray-200'
            }`}>
            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${practitioner.isActive ? 'bg-green-400' : 'bg-gray-400'
              }`}></div>
            {practitioner.isActive ? '在职' : '离职'}
          </span>
        ) : null
      )
    },
    {
      key: 'actions',
      title: '操作',
      render: (cellValue, practitioner) => (
        canManagePractitioners && practitioner ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleToggleStatus(practitioner)}
              className="text-yellow-600 hover:text-yellow-700 text-sm font-medium transition-colors duration-200"
            >
              {practitioner.isActive ? '离职' : '恢复'}
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => window.location.href = `/practitioners/${practitioner.id}`}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200"
            >
              查看
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => handleDeletePractitioner(practitioner.id)}
              className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
            >
              删除
            </button>
          </div>
        ) : null
      ),
      cellClassName: 'text-right'
    }
  ];

  // Filter options
  const filterOptions = [
    { value: 'all', label: '全部调理师' },
    { value: 'active', label: '在职调理师' },
    { value: 'inactive', label: '离职调理师' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              调理师管理
            </h1>
            <p className="text-gray-600">
              管理中心的调理师信息和状态
            </p>
          </div>
          {canManagePractitioners && (
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleAddNewPractitioner}
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加调理师
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="搜索调理师姓名、职称或专长..."
                className="w-full"
              />
            </div>
            <div>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                options={filterOptions}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>共找到 {filteredPractitioners.length} 名调理师</span>
            {searchTerm && (
              <span>搜索: "{searchTerm}"</span>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Practitioners Table */}
        <Table
          columns={columns}
          data={filteredPractitioners}
          loading={loading}
          variant="simple"
          className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
          emptyMessage={searchTerm ? '没有找到匹配的调理师' : '还没有调理师'}
          emptyIcon={
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          emptyAction={
            canManagePractitioners && !searchTerm ? (
              <Button onClick={handleAddNewPractitioner} className="mt-4">
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加第一个调理师
              </Button>
            ) : null
          }
        />

        {/* Add/Edit Practitioner Modal */}
        <Modal
          isOpen={showPractitionerModal}
          onClose={() => setShowPractitionerModal(false)}
          title={selectedPractitioner ? '编辑调理师信息' : '添加新调理师'}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {formErrors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {formErrors.submit}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!selectedPractitioner && (
                <>
                  <Input
                    label="用户名"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    error={formErrors.username}
                    required
                    placeholder="用于登录的用户名"
                  />
                  <Input
                    label="密码"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={formErrors.password}
                    required
                    placeholder="登录密码"
                  />
                </>
              )}

              <div className={selectedPractitioner ? "md:col-span-2" : ""}>
                <Input
                  label="姓名"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={formErrors.name}
                  required
                  placeholder="请输入调理师姓名"
                />
              </div>

              <Input
                label="职称"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="如：主任医师、副主任医师等"
              />

              <Input
                label="联系电话"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                error={formErrors.phoneNumber}
                placeholder="请输入手机号码"
              />

              <div className="md:col-span-2">
                <Input
                  label="电子邮箱"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={formErrors.email}
                  placeholder="请输入邮箱地址"
                />
              </div>

              <div className="md:col-span-2">
                <Input
                  label="专长领域"
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  placeholder="如：针灸、推拿、调理、艾灸等"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  个人简介
                </label>
                <textarea
                  name="bio"
                  rows={4}
                  value={formData.bio}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors resize-none"
                  placeholder="调理师的工作经验、背景、专业资质等"
                />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    此调理师处于在职状态
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowPractitionerModal(false)}
                className="flex-1"
              >
                取消
              </Button>
              <Button
                type="submit"
                className="flex-1"
              >
                {selectedPractitioner ? '保存修改' : '添加调理师'}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default PractitionersPage;